import type { AxiosError } from "axios";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

type DRFErrorResponse = {
	response?: {
		data?: Record<string, string | string[]>;
	};
};

export function setDrfErrors<T extends FieldValues>(
	error: unknown,
	setError: UseFormSetError<T>,
) {
	const data = (error as DRFErrorResponse)?.response?.data;
	if (!data || typeof data !== "object") return;

	for (const [field, messages] of Object.entries(data)) {
		const message = Array.isArray(messages) ? messages[0] : messages;
		if (message) {
			setError(field as Path<T>, { message: String(message) });
		}
	}
}

export function getDrfFieldErrors(error: unknown): Record<string, string> {
	const data = (error as DRFErrorResponse)?.response?.data;
	if (!data || typeof data !== "object") return {};

	const result: Record<string, string> = {};
	for (const [field, messages] of Object.entries(data)) {
		const message = Array.isArray(messages) ? messages[0] : messages;
		if (message) {
			result[field] = String(message);
		}
	}
	return result;
}

const STATUS_TO_KEY: Record<number, string> = {
	400: "errors.badRequest",
	401: "errors.unauthorized",
	403: "errors.forbidden",
	404: "errors.notFound",
	409: "errors.conflict",
	422: "errors.validation",
	429: "errors.tooManyRequests",
};

export function getApiErrorKey(error: unknown): string {
	const axiosError = error as AxiosError;

	if (!axiosError?.response) {
		return "errors.network";
	}

	const status = axiosError.response.status;

	if (status && STATUS_TO_KEY[status]) {
		return STATUS_TO_KEY[status];
	}

	if (status && status >= 500) {
		return "errors.server";
	}

	return "errors.unknown";
}
